import { ReportSdk } from 'lib-klab-sdks';

type DateRange = { startDate: string; endDate?: string };

function buildDefaultRange(): DateRange {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 30);
    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
    };
}

function mockExportBlob(payload: { format: string }): Blob {
    const fmt = String(payload.format ?? 'CSV').toUpperCase();
    if (fmt === 'PDF') {
        return new Blob(['%PDF-1.4\n%.mock\n'], { type: 'application/pdf' });
    }
    const csv =
        'codigo,nombres,locacion,departamento,fecha,horaInicio,horaFin,horasTotales,productividad\nEMP001,Juan Pérez,INTECH,Desarrollo,2025-01-15,09:00,18:00,8,76.92';
    return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
}

export const adminReportsSdkMock = {
    getAdminActivitiesSummary: (
        payload: {
            type: ReportSdk.ActivityTypeFilterEnum;
            dateRange: DateRange;
            locationId?: number;
            departmentId?: number;
            search?: string;
        },
    ): ReportSdk.AdminActivitySummaryResponse => {
        const type = payload.type ?? ReportSdk.ActivityTypeFilterEnum.Web;
        const webs: ReportSdk.AdminActivitySummaryItem[] = [
            { identifier: 'github.com', totalSeconds: 5 * 3600, classification: 'PRODUCTIVE' },
            { identifier: 'youtube.com', totalSeconds: 3 * 3600, classification: 'UNPRODUCTIVE' },
            { identifier: 'facebook.com', totalSeconds: 1 * 3600, classification: 'UNPRODUCTIVE' },
        ];
        const apps: ReportSdk.AdminActivitySummaryItem[] = [
            { identifier: 'Visual Studio Code', totalSeconds: 6 * 3600, classification: 'PRODUCTIVE' },
            { identifier: 'Slack', totalSeconds: 2 * 3600, classification: 'NEUTRAL' },
        ];
        const content = type === ReportSdk.ActivityTypeFilterEnum.App ? apps : webs;
        const search = payload.search?.trim().toLowerCase();
        const filtered = search
            ? content.filter((item) => item.identifier.toLowerCase().includes(search))
            : content;
        return { type, content: filtered };
    },

    getAdminActivityDetail: (
        payload: {
            type: ReportSdk.ActivityTypeFilterEnum;
            identifier: string;
            dateRange: DateRange;
            locationId?: number;
            departmentId?: number;
        },
    ): ReportSdk.AdminActivityDetailResponse => {
        const employees: ReportSdk.AdminActivityEmployeeItem[] = [
            { employeeId: 1, code: 'IN0025', fullName: 'Piero Rengifo', totalSeconds: 2 * 3600 },
            { employeeId: 2, code: 'IN0031', fullName: 'María García', totalSeconds: 1 * 3600 },
        ];
        const totalSeconds = employees.reduce((acc, item) => acc + item.totalSeconds, 0);
        return {
            type: payload.type ?? ReportSdk.ActivityTypeFilterEnum.Web,
            identifier: payload.identifier,
            totalSeconds,
            employees,
        };
    },

    getAdminTimesheetsList: (
        payload: {
            dateRange: DateRange;
            locationId?: number;
            departmentId?: number;
            search?: string;
            page?: number;
            size?: number;
        },
    ): ReportSdk.AdminTimesheetResponse => {
        const range = payload.dateRange ?? buildDefaultRange();
        const page = Math.max(0, payload.page ?? 0);
        const size = Math.max(1, payload.size ?? 20);

        const content: ReportSdk.AdminTimesheetItem[] = [
            {
                employee: { employeeId: 1, code: 'EMP001', firstName: 'Juan', lastName: 'Pérez' },
                location: { locationId: 1, name: 'INTECH' },
                department: { departmentId: 1, name: 'Desarrollo' },
                date: range.startDate,
                startTime: '09:00:00',
                endTime: '18:00:00',
                totalSeconds: 8 * 3600,
                officeSeconds: Math.floor(7.5 * 3600),
                activeSeconds: Math.floor(6.5 * 3600),
                productiveSeconds: 5 * 3600,
                unproductiveSeconds: 1 * 3600,
                neutralSeconds: Math.floor(0.5 * 3600),
                idleSeconds: Math.floor(0.5 * 3600),
                offlineSeconds: Math.floor(0.5 * 3600),
                productivityPercentage: 76.92,
            },
            {
                employee: { employeeId: 2, code: 'EMP002', firstName: 'María', lastName: 'García' },
                location: { locationId: 1, name: 'INTECH' },
                department: { departmentId: 2, name: 'QA' },
                date: range.startDate,
                startTime: '08:30:00',
                endTime: '17:30:00',
                totalSeconds: 9 * 3600,
                officeSeconds: Math.floor(8.5 * 3600),
                activeSeconds: 7 * 3600,
                productiveSeconds: Math.floor(5.5 * 3600),
                unproductiveSeconds: 1 * 3600,
                neutralSeconds: Math.floor(0.5 * 3600),
                idleSeconds: Math.floor(0.5 * 3600),
                offlineSeconds: Math.floor(0.5 * 3600),
                productivityPercentage: 78.57,
            },
        ];

        const totalElements = content.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / size));
        const start = page * size;
        const pagedContent = content.slice(start, start + size);

        return {
            metadata: {
                locationId: payload.locationId,
                departmentId: payload.departmentId,
                search: payload.search,
                dateRange: range,
            },
            content: pagedContent,
            page: { page, size, totalElements, totalPages },
        } as ReportSdk.AdminTimesheetResponse;
    },

    exportAdminTimesheetsList: (payload: {
        format: string;
        dateRange?: DateRange;
        locationId?: number;
        departmentId?: number;
        search?: string;
    }): Blob => mockExportBlob(payload),

    exportAdminDownloadList: (payload: {
        format: string;
        dateRange?: DateRange;
        locationId?: number;
        departmentId?: number;
        roleId?: number;
        search?: string;
        extraFields?: string[];
    }): Blob => mockExportBlob(payload),

    exportAdminDownloadEmployee: (payload: {
        format: string;
        employeeId?: number;
        dateRange?: DateRange;
        search?: string;
    }): Blob => mockExportBlob(payload),

    exportProductivityEmployees: (payload: {
        format: string;
        dateRange?: DateRange;
        locationId?: number;
        departmentId?: number;
        employeeId?: number;
        search?: string;
    }): Blob => mockExportBlob(payload),

    exportProductivityDepartment: (payload: {
        format: string;
        dateRange?: DateRange;
        locationId?: number;
        departmentId?: number;
        search?: string;
    }): Blob => mockExportBlob(payload),

    exportProductivityLocation: (payload: {
        format: string;
        dateRange?: DateRange;
        locationId?: number;
        departmentId?: number;
        search?: string;
    }): Blob => mockExportBlob(payload),

    getAdminDownloadEmployee: (
        req: ReportSdk.AdminDownloadEmployeeRequest,
    ): ReportSdk.AdminDownloadEmployeeResponse => {
        const range = req.dateRange ?? buildDefaultRange();
        const page = Math.max(0, req.page ?? 0);
        const size = Math.max(1, req.size ?? 10);
        const content: ReportSdk.AdminDownloadEmployeeItem[] = [
            {
                name: 'Sesión productiva',
                title: 'Desarrollo',
                startAt: `${range.startDate}T14:00:00.000Z`,
                endedAt: `${range.endDate ?? range.startDate}T18:00:00.000Z`,
                activeSeconds: 4 * 3600,
                idleSeconds: 15 * 60,
                totalSeconds: 4 * 3600 + 15 * 60,
            },
        ];
        return {
            metadata: {
                employeeId: req.employeeId,
                search: req.search,
                dateRange: range,
            },
            content,
            page: { page, size, totalElements: content.length, totalPages: 1 },
        } as ReportSdk.AdminDownloadEmployeeResponse;
    },

    getEmployeeProductivityMetrics: (
        req: ReportSdk.AdminProductivityRequest,
    ): ReportSdk.EmployeeProductivityMetricResponse => ({
        metrics: [
            { name: 'Productivo', productiveSeconds: 5 * 3600, unproductiveSeconds: 3600, neutralSeconds: 600, idleSeconds: 300 },
            { name: 'Neutral', productiveSeconds: 2 * 3600, unproductiveSeconds: 2 * 3600, neutralSeconds: 3 * 3600, idleSeconds: 600 },
        ],
    }),

    getOrganizationProductivityMetrics: (
        _req: ReportSdk.AdminProductivityRequest,
    ): ReportSdk.LocationProductivityMetricResponse => ({
        metrics: [
            { location: 'INTECH', department: 'Desarrollo', productiveSeconds: 40 * 3600, unproductiveSeconds: 10 * 3600, neutralSeconds: 5 * 3600, idleSeconds: 2 * 3600 },
            { location: 'KEOLA', department: 'QA', productiveSeconds: 30 * 3600, unproductiveSeconds: 8 * 3600, neutralSeconds: 4 * 3600, idleSeconds: 2 * 3600 },
        ],
    }),

    getAdminDownloadList: (
        payload: {
            dateRange?: DateRange;
            locationId?: number;
            departmentId?: number;
            roleId?: number;
            search?: string;
            page?: number;
            size?: number;
        },
    ): ReportSdk.AdminDownloadResponse => {
        const range = payload.dateRange ?? buildDefaultRange();
        const page = Math.max(0, payload.page ?? 0);
        const size = Math.max(1, payload.size ?? 20);

        const downloadMetrics = (
            productivePct: number,
        ): Pick<
            ReportSdk.AdminDownloadItem,
            | 'startAt'
            | 'endedAt'
            | 'totalSeconds'
            | 'officeSeconds'
            | 'activeSeconds'
            | 'productiveSeconds'
            | 'unproductiveSeconds'
            | 'neutralSeconds'
            | 'idleSeconds'
            | 'offlineSeconds'
            | 'totalWorkDaySeconds'
            | 'productivityPercentage'
        > => {
            const totalSeconds = 8 * 3600;
            const productiveSeconds = Math.round((productivePct / 100) * totalSeconds);
            const unproductiveSeconds = Math.round(0.15 * totalSeconds);
            const neutralSeconds = Math.round(0.1 * totalSeconds);
            const idleSeconds = Math.max(0, totalSeconds - productiveSeconds - unproductiveSeconds - neutralSeconds);
            return {
                startAt: `${range.startDate}T14:00:00.000Z`,
                endedAt: `${range.endDate ?? range.startDate}T22:00:00.000Z`,
                totalSeconds,
                officeSeconds: totalSeconds,
                activeSeconds: totalSeconds - Math.floor(0.05 * totalSeconds),
                productiveSeconds,
                unproductiveSeconds,
                neutralSeconds,
                idleSeconds,
                offlineSeconds: 0,
                totalWorkDaySeconds: totalSeconds,
                productivityPercentage: productivePct,
            };
        };

        const content: ReportSdk.AdminDownloadItem[] = [
            {
                employee: {
                    employeeId: 1,
                    code: 'EMP001',
                    firstName: 'Juan',
                    lastName: 'Pérez',
                    email: 'juan.perez@example.com',
                },
                location: { locationId: 1, name: 'INTECH' },
                department: { departmentId: 1, name: 'Desarrollo' },
                role: { roleId: 1, name: 'Desarrollador' },
                ...downloadMetrics(72),
            },
            {
                employee: {
                    employeeId: 2,
                    code: 'EMP002',
                    firstName: 'María',
                    lastName: 'García',
                    email: 'maria.garcia@example.com',
                },
                location: { locationId: 1, name: 'INTECH' },
                department: { departmentId: 2, name: 'QA' },
                role: { roleId: 2, name: 'QA Engineer' },
                ...downloadMetrics(65),
            },
            {
                employee: {
                    employeeId: 3,
                    code: 'EMP003',
                    firstName: 'Carlos',
                    lastName: 'López',
                    email: 'carlos.lopez@example.com',
                },
                location: { locationId: 2, name: 'KEOLA' },
                department: { departmentId: 1, name: 'Desarrollo' },
                role: { roleId: 1, name: 'Desarrollador' },
                ...downloadMetrics(58),
            },
        ];

        const totalElements = content.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / size));
        const start = page * size;
        const pagedContent = content.slice(start, start + size);

        return {
            metadata: {
                locationId: payload.locationId,
                departmentId: payload.departmentId,
                roleId: payload.roleId,
                search: payload.search,
                dateRange: range,
            },
            content: pagedContent,
            page: { page, size, totalElements, totalPages },
        } as ReportSdk.AdminDownloadResponse;
    },

    getEmployeeProductivity: (
        payload: {
            dateRange: DateRange;
            locationId?: number;
            departmentId?: number;
            search?: string;
            page?: number;
            size?: number;
        },
    ): ReportSdk.EmployeeProductivityResponse => {
        const range = payload.dateRange ?? buildDefaultRange();
        const page = Math.max(0, payload.page ?? 0);
        const size = Math.max(1, payload.size ?? 20);

        const content: ReportSdk.EmployeeProductivityItem[] = [
            {
                employee: { employeeId: 1, firstName: 'Juan', lastName: 'Pérez' },
                startDate: range.startDate,
                endDate: range.endDate ?? range.startDate,
                officeSeconds: 8 * 3600,
                productiveSeconds: Math.floor(5.5 * 3600),
                productivityPercentage: 68.75,
                unproductiveSeconds: Math.floor(1.5 * 3600),
                unproductivePercentage: 18.75,
                neutralSeconds: Math.floor(0.5 * 3600),
                idleSeconds: Math.floor(0.5 * 3600),
            } as ReportSdk.EmployeeProductivityItem,
            {
                employee: { employeeId: 2, firstName: 'María', lastName: 'García' },
                startDate: range.startDate,
                endDate: range.endDate ?? range.startDate,
                officeSeconds: Math.floor(7.5 * 3600),
                productiveSeconds: 6 * 3600,
                productivityPercentage: 80,
                unproductiveSeconds: Math.floor(0.8 * 3600),
                unproductivePercentage: 10.67,
                neutralSeconds: Math.floor(0.4 * 3600),
                idleSeconds: Math.floor(0.3 * 3600),
            } as ReportSdk.EmployeeProductivityItem,
        ];

        const totalElements = content.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / size));
        const start = page * size;
        const pagedContent = content.slice(start, start + size);

        return {
            metadata: { dateRange: range, locationId: payload.locationId, departmentId: payload.departmentId, search: payload.search },
            content: pagedContent,
            page: { page, size, totalElements, totalPages },
        } as ReportSdk.EmployeeProductivityResponse;
    },

    getOrganizationProductivity: (
        payload: {
            dateRange: DateRange;
            locationId?: number;
            departmentId?: number;
            search?: string;
            page?: number;
            size?: number;
        },
    ): ReportSdk.OrganizationProductivityResponse => {
        const range = payload.dateRange ?? buildDefaultRange();
        const page = Math.max(0, payload.page ?? 0);
        const size = Math.max(1, payload.size ?? 20);

        const withDept =
            payload.locationId != null &&
            (payload.departmentId == null || String(payload.departmentId).trim() === '');

        const content: ReportSdk.OrganizationProductivityItem[] = [
            {
                location: { locationId: 1, name: 'INTECH' },
                ...(withDept ? { department: { departmentId: 10, name: 'TI - Software' } } : {}),
                startDate: range.startDate,
                endDate: range.endDate ?? range.startDate,
                officeSeconds: 120 * 3600,
                productiveSeconds: 85 * 3600,
                productivityPercentage: 70.83,
                unproductiveSeconds: 20 * 3600,
                unproductivePercentage: 16.67,
                neutralSeconds: 8 * 3600,
                idleSeconds: 7 * 3600,
            } as ReportSdk.OrganizationProductivityItem,
            {
                location: { locationId: 1, name: 'INTECH' },
                ...(withDept ? { department: { departmentId: 11, name: 'QA - Software' } } : {}),
                startDate: range.startDate,
                endDate: range.endDate ?? range.startDate,
                officeSeconds: 95 * 3600,
                productiveSeconds: 72 * 3600,
                productivityPercentage: 75.79,
                unproductiveSeconds: 12 * 3600,
                unproductivePercentage: 12.63,
                neutralSeconds: 6 * 3600,
                idleSeconds: 5 * 3600,
            } as ReportSdk.OrganizationProductivityItem,
        ];

        const totalElements = content.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / size));
        const start = page * size;
        const pagedContent = content.slice(start, start + size);

        return {
            metadata: { dateRange: range, locationId: payload.locationId, departmentId: payload.departmentId, search: payload.search },
            content: pagedContent,
            page: { page, size, totalElements, totalPages },
        } as ReportSdk.OrganizationProductivityResponse;
    },
    
    getEmployeeDailyProductivity: (
        req: ReportSdk.AdminProductivityRequest,
    ): ReportSdk.EmployeeProductivityResponse => {
        const range = req.dateRange ?? buildDefaultRange();
        // Genera un item por día del rango para simular el desglose diario
        const start = new Date(range.startDate);
        const end   = new Date(range.endDate ?? range.startDate);
        const items: ReportSdk.EmployeeProductivityItem[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const date = d.toISOString().slice(0, 10);
            items.push({
                employee: {
                    employeeId: req.employeeId ?? 1,
                    firstName:  'Juan',
                    lastName:   'Pérez',
                },
                startDate:               date,
                endDate:                 date,
                officeSeconds:           Math.floor(7.5 * 3600),
                productiveSeconds:       5 * 3600,
                productivityPercentage:  76.92,
                unproductiveSeconds:     1 * 3600,
                unproductivePercentage:  15.38,
                neutralSeconds:          Math.floor(0.5 * 3600),
                idleSeconds:             Math.floor(0.5 * 3600),
            });
        }
        return {
            metadata: {
                dateRange:    range,
                locationId:   req.locationId,
                departmentId: req.departmentId,
                search:       req.search,
            },
            content: items,
            page: { page: 0, size: items.length, totalElements: items.length, totalPages: 1 },
        };
    },

    getEmployeeDailyMetrics: (
        req: ReportSdk.AdminProductivityRequest,
    ): ReportSdk.EmployeeDailyMetricResponse => {
        const range = req.dateRange ?? buildDefaultRange();
        const start = new Date(range.startDate);
        const end   = new Date(range.endDate ?? range.startDate);
        const metrics: ReportSdk.EmployeeDailyMetricItem[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            metrics.push({
                date:                d.toISOString().slice(0, 10),
                productiveSeconds:   5 * 3600,
                unproductiveSeconds: 1 * 3600,
                neutralSeconds:      Math.floor(0.5 * 3600),
                idleSeconds:         Math.floor(0.5 * 3600),
            });
        }
        return { metrics };
    },
};
